import {
  TrashIcon,
  ArrowLeftStartOnRectangleIcon,
} from '@heroicons/react/24/solid';
import {
  Node,
  useStore,
  Position,
  NodeProps,
  NodeToolbar,
  useStoreApi,
  NodeResizer,
  useReactFlow,
  OnResizeStart,
} from '@xyflow/react';
import { memo, useCallback } from 'react';
import { Button } from 'src/components/button';
import { GroupIcon } from 'src/components/icons/GroupIcon';
import { useUndoRedo } from 'src/context/undo-redo';
import useDetachNodes from 'src/hooks/useDetachNodes';
import { getRelativeNodesBounds } from 'src/utils/node';
import { twMerge } from 'tailwind-merge';

import './group-node.scss';

const lineStyle = { borderColor: 'white' };

function GroupNode({ id, selected, ...rest }: NodeProps) {
  const store = useStoreApi();
  const { takeSnapshot } = useUndoRedo();
  const { deleteElements } = useReactFlow();
  const detachNodes = useDetachNodes();
  const { minWidth, minHeight, hasChildNodes } = useStore((_store: any) => {
    const childNodes = Array.from(_store.nodeLookup.values()).filter(
      (n: any) => n.parentId === id,
    );
    const rect = getRelativeNodesBounds(childNodes as Node[]);

    return {
      minWidth: rect.x + rect.width,
      minHeight: rect.y + rect.height,
      hasChildNodes: childNodes.length > 0,
    };
  }, isEqual);

  const onDelete = () => {
    takeSnapshot();
    deleteElements({ nodes: [{ id }] });
  };

  const onUngroup = () => {
    takeSnapshot();
    const childNodeIds = Array.from(store.getState().nodeLookup.values())
      .filter((n) => n.parentId === id)
      .map((n) => n.id);

    detachNodes(childNodeIds, id);
  };

  const hasParent = store.getState().nodeLookup.get(id)?.parentId;

  const onDetach = () => {
    takeSnapshot();
    detachNodes([id]);
  };

  const onResizeStart: OnResizeStart = useCallback(
    (event, node) => {
      takeSnapshot();
    },
    [takeSnapshot],
  );

  return (
    <div className={twMerge('group-node')}>
      <NodeResizer
        lineStyle={lineStyle}
        minHeight={minHeight}
        minWidth={minWidth}
        onResizeStart={onResizeStart}
      />
      <NodeToolbar className="nodrag" position={Position.Bottom}>
        <div className="flex gap-2">
          <Button
            data-tooltip-id="app-tooltip"
            data-tooltip-content="Delete"
            color="red"
            onClick={onDelete}
          >
            <TrashIcon className="h-4 w-4" />
          </Button>
          {hasChildNodes && (
            <Button
              data-tooltip-id="app-tooltip"
              data-tooltip-content="Ungroup"
              color="blue"
              onClick={onUngroup}
            >
              <GroupIcon className="h-4 w-4" />
            </Button>
          )}
          {hasParent && (
            <Button
              data-tooltip-id="app-tooltip"
              data-tooltip-content="Detach"
              color="orange"
              onClick={onDetach}
            >
              <ArrowLeftStartOnRectangleIcon className="h-4 w-4" />
            </Button>
          )}
        </div>
      </NodeToolbar>
    </div>
  );
}

type IsEqualCompareObj = {
  minWidth: number;
  minHeight: number;
  hasChildNodes: boolean;
};

function isEqual(prev: IsEqualCompareObj, next: IsEqualCompareObj): boolean {
  return (
    prev.minWidth === next.minWidth &&
    prev.minHeight === next.minHeight &&
    prev.hasChildNodes === next.hasChildNodes
  );
}

export default memo(GroupNode);
